import { useQueryClient } from '@tanstack/react-query';

import { useFishGroupsController_operate } from '@/services/hooks';
import type { OperationResponseDto } from '@/services/types';

import type { MutationState } from '../shared/mutation';

import { invalidateFishGroupQueries } from './_invalidateQueries';
import type { FishGroupOperationRequest } from './operationTypes';

export interface UseOperateFishGroupResult extends MutationState {
    operate: (requestBody: FishGroupOperationRequest) => Promise<OperationResponseDto>;
}

interface UseOperateFishGroupOptions {
    onSuccess?: (response: OperationResponseDto) => void;
    onError?: (error: unknown) => void;
}

/**
 * Mutation hook for the composition-changing operation endpoint
 * (`POST /:clientId/operate`). Differs from the simpler action hooks
 * (close / discard / relocate) in that one call impacts many fish groups
 * at once: every source is closed and every destination is created. We
 * therefore invalidate read queries for **all** impacted FGs, not just
 * one.
 *
 * The generated SDK types the response as `any`; we cast at the boundary
 * here so the caller gets a proper `OperationResponseDto`.
 */
export const useOperateFishGroup = (
    clientId: number,
    options?: UseOperateFishGroupOptions
): UseOperateFishGroupResult => {
    const queryClient = useQueryClient();
    const mutation = useFishGroupsController_operate();

    const operate = async (
        requestBody: FishGroupOperationRequest
    ): Promise<OperationResponseDto> => {
        try {
            const result = await mutation.mutateAsync({ clientId, requestBody });
            const response = result.data as OperationResponseDto;

            // Refresh the overview once, plus per-FG queries for every
            // source (now closed) and every destination (newly created)
            // so the detail / events / lineage / locations views are
            // accurate after the operation completes.
            const impactedIds = [
                ...response.sourcesClosed.map((s) => s.id),
                ...response.destinationsCreated.map((d) => d.id),
            ];
            if (impactedIds.length === 0) {
                await invalidateFishGroupQueries(queryClient, clientId);
            } else {
                await Promise.all(
                    impactedIds.map((id) => invalidateFishGroupQueries(queryClient, clientId, id))
                );
            }

            options?.onSuccess?.(response);
            return response;
        } catch (error) {
            options?.onError?.(error);
            throw error;
        }
    };

    return {
        operate,
        isLoading: mutation.isPending,
        error: mutation.error,
        reset: mutation.reset,
    };
};
